<template>
    <div>
        <kgr-form-scaffold
            :with-back-button="false"
            :pending="pending"
            :save-button-disabled="!validFormValues"
            @primaryBtnClicked="updateLinks"
        >
            <kgr-note-box
                title-key="clientSystemLink.note.title"
                text-key="clientSystemLink.note.text"
            />
            <br />
            <table class="kgr-table kgr-table--striped kgr-table--block table-groups kgr-row drag-wrapper">
                <thead>
                    <tr class="kgr-table__row">
                        <th
                            v-t="$t('clientSystemLink')"
                            class="kgr-table__cell kgr-table__cell--headline"
                        >
                        </th>
                        <th
                            v-t="$t('Aktionen')"
                            class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions table-actions"
                        >
                        </th>
                    </tr>
                </thead>
                <draggable
                    v-model="clientSystemLinks"
                    tag="tbody"
                    handle=".handle"
                    @start="onDrag"
                    @end="onDrop"
                >
                    <tr
                        v-for="clientSystemLink in clientSystemLinks"
                        :key="clientSystemLink.id"
                        :data-id="clientSystemLink.id"
                        class="kgr-table__row"
                        :class="{ deleted: clientSystemLink.deleted }"
                    >
                        <td class="kgr-table__cell handle-column">
                            <div class="handle extra-padding">
                                <img
                                    v-if="clientSystemLink.iconImageFile"
                                    :src="clientSystemLink.iconImageFile"
                                    class="icon-image-file"
                                    alt=""
                                />
                                <kgr-icon
                                    v-else-if="clientSystemLink.icon"
                                    :icon-class="clientSystemLink.icon"
                                    class="cls-icon"
                                />
                                {{ clientSystemLink.label }}
                            </div>
                            <template v-if="!clientSystemLink.deleted && (edit === clientSystemLink || !clientSystemLink.error.isEmpty())">
                                <div class="extra-padding">
                                    <div
                                        v-if="clientSystemLink.iconImageFile === null"
                                        class="kgr-inputGroup "
                                    >
                                        <div class="kgr-inputGroup__label">
                                            {{ $t('clientSystemLink.icon') }}
                                        </div>
                                        <kgr-icon-select
                                            v-model="clientSystemLink.icon"
                                            :options="iconOptions"
                                        />
                                    </div>
                                    <kgr-input-group
                                        :label="$t('clientSystemLink.label')"
                                        :error-message="clientSystemLink.error.messageForField('label')"
                                    >
                                        <input
                                            v-model="clientSystemLink.label"
                                            class="kgr-input"
                                            type="text"
                                            required
                                            @input="validate()"
                                        />
                                    </kgr-input-group>
                                    <kgr-input-group
                                        :label="$t('clientSystemLink.url')"
                                        :error-message="clientSystemLink.error.messageForField('url')"
                                    >
                                        <input
                                            v-model="clientSystemLink.url"
                                            class="kgr-input"
                                            type="text"
                                            required
                                            @input="validate()"
                                        />
                                        <div
                                            class="kgr-inputGroup__text--belowInput"
                                            v-html="$t('clientSystemLink.url.help')"
                                        ></div>
                                    </kgr-input-group>
                                    <br />
                                    <div class="kgr-grid kgr-grid--1of2 kgr-grid--gutters kgr-grid--bigGutters">
                                        <div class="kgr-grid__cell">
                                            <div class="kgr-inputGroup">
                                                <div class="kgr-inputGroup__label">
                                                    {{ $t('Abteilungen') }}
                                                </div>
                                                <kgr-multi-select
                                                    v-model="clientSystemLink.departmentObjects"
                                                    :options="departments"
                                                    display="extendedName"
                                                    remove-selected
                                                />
                                                <div class="kgr-inputGroup__text--belowInput">
                                                    {{ $t('Wähle hier Abteilungen aus, wenn der Link nur für bestimmte Abteilungen sichtbar sein soll.') }}
                                                </div>
                                            </div>
                                        </div>
                                        <div class="kgr-grid__cell">
                                            <div class="kgr-inputGroup">
                                                <div class="kgr-inputGroup__label">
                                                    {{ $t('clientSystemLink.contractTypes') }}
                                                </div>
                                                <kgr-multi-select
                                                    :value="clientSystemLink.contractTypes.map(contractType => contractTypes.find(ct => ct.value === contractType))"
                                                    :options="contractTypes"
                                                    display="label"
                                                    remove-selected
                                                    @post-input="onContractType(clientSystemLink, $event)"
                                                />
                                                <div class="kgr-inputGroup__text--belowInput">
                                                    {{ $t('Wähle hier Vertragsarten aus, wenn der Link nur für bestimmte Vertragsarten sichtbar sein soll.') }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--actions table-actions">
                            <button
                                v-if="!clientSystemLink.deleted"
                                type="button"
                                class="kgr-table__action"
                                @click="editLink(clientSystemLink)"
                            >
                                <kgr-icon-edit />
                            </button>
                            <button
                                v-if="clientSystemLink.deleted"
                                type="button"
                                class="kgr-table__action"
                                @click="undoDelete(clientSystemLink)"
                            >
                                <kgr-icon-undo />
                            </button>
                            <button
                                v-else
                                type="button"
                                class="kgr-table__action"
                                @click="deleteLink(clientSystemLink)"
                            >
                                <kgr-icon-delete />
                            </button>
                        </td>
                    </tr>
                </draggable>
            </table>

            <kgr-button
                :disabled="false"
                @click="createLink()"
            >
                {{ $t('Neuen Link erstellen') }}
            </kgr-button>
        </kgr-form-scaffold>
    </div>
</template>

<script>
import ClubClientSystemLink from '../../js/accounting/dto/ClubClientSystemLink.js';
import ClubClientSystemLinkRepository from '../../js/accounting/repository/ClubClientSystemLinkRepository.js';
import DepartmentRepository from '../../js/repository/DepartmentRepository.js';
import Error from '../../js/model/Error.js';
import FormScaffold from '../../components/common/form/FormScaffold.vue';
import Icon from '../../components/common/icon/Icon.vue';
import IconDelete from '../../components/common/icon/IconDelete.vue';
import IconEdit from '../../components/common/icon/IconEdit.vue';
import IconReset from '../../components/common/icon/IconReset.vue';
import IconSelect from '../../components/common/form/select/IconSelect.vue';
import InputGroup from '../../components/common/form/InputGroup.vue';
import KgrButton from '../../components/common/form/button/KgrButton.vue';
import MultiSelect from '../../components/common/form/select/MultiSelect.vue';
import NoteBox from '../../components/common/NoteBox.vue';
import draggable from 'vuedraggable';

export default {
    components: {
        'kgr-button': KgrButton,
        'kgr-form-scaffold': FormScaffold,
        'kgr-icon': Icon,
        'kgr-icon-select': IconSelect,
        'kgr-icon-delete': IconDelete,
        'kgr-multi-select': MultiSelect,
        'kgr-icon-edit': IconEdit,
        'kgr-note-box': NoteBox,
        'kgr-icon-undo': IconReset,
        draggable,
        'kgr-input-group': InputGroup,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            contractTypes: [
                { value: 'regular', label: this.$t('memberShortProfile.contractType.regular') },
                { value: 'passive', label: this.$t('memberShortProfile.contractType.passive') },
                { value: 'guest', label: this.$t('memberShortProfile.contractType.guest') },
            ],
            departments: [],
            pending: false,
            dragging: false,
            validFormValues: true,
            edit: null,
            clientSystemLinks: [],
            iconOptions: [
                'bar-bell',
                'list-check',
                'sports-tennis',
                'storefront',
                'star-circle',
                'info-circle',
                'shopping-cart',
                'date_range',
                'restaurant',
                't-shirt',
            ],
        };
    },
    mounted() {
        this.pending = true;
        DepartmentRepository.findAll(this.clubId)
            .then((response) => {
                this.departments = this.getFlattenedDepartments(response);
                this.loadLinks();
            });
    },
    methods: {
        validate() {
            this.clientSystemLinks.forEach((clientSystemLink) => {
                clientSystemLink.error.clear('label');
                clientSystemLink.error.clear('url');
                if (clientSystemLink.deleted) {
                    return;
                }
                if (clientSystemLink.label === '') {
                    clientSystemLink.error.add('label', this.$t('Dieser Wert sollte nicht leer sein.'));
                }
                if (!this.isUrlValid(clientSystemLink.url)) {
                    clientSystemLink.error.add('url', this.$t('Geben Sie eine gültige URL ein.'));
                }
            });
            this.validFormValues = this.clientSystemLinks
                .filter((clientSystemLink) => clientSystemLink.deleted !== true)
                .filter((clientSystemLink) => !clientSystemLink.error.isEmpty())
                .length === 0;
        },
        loadLinks() {
            this.pending = true;
            ClubClientSystemLinkRepository.findByClub(this.clubId)
                .then((clientSystemLinks) => {
                    clientSystemLinks.forEach((clientSystemLink) => {
                        clientSystemLink.departmentObjects = clientSystemLink.departments.map((departmentId) => this.departments.find((d) => d.id === departmentId));
                        clientSystemLink.error = new Error();
                    });
                    this.clientSystemLinks = clientSystemLinks; // remove departmentObjects from clientSystemLink.js
                    this.pending = false;
                });
        },
        onDrag(event) {
            this.dragging = true;
            event.item.classList.add('dragging');
        },
        onDrop(event) {
            this.dragging = false;
            event.item.classList.remove('dragging');
            this.reorderLinks();
        },
        onContractType(clientSystemLink, contractTypes) {
            clientSystemLink.contractTypes = contractTypes.map((contractType) => contractType.value);
        },
        getFlattenedDepartments(departments) {
            const allDepartments = [];
            function collectIds(departments) {
                departments.forEach((department) => {
                    allDepartments.push(department);
                    if (department.childDepartments && department.childDepartments.length > 0) {
                        collectIds(department.childDepartments);
                    }
                });
            }

            collectIds(departments);
            return allDepartments;
        },
        reorderLinks() {
            this.clientSystemLinks.forEach((clientSystemLink, index) => {
                clientSystemLink.position = index;
            });
        },
        createLink() {
            const clientSystemLink = new ClubClientSystemLink();
            clientSystemLink.icon = this.iconOptions[0];
            clientSystemLink.departmentObjects = [];
            clientSystemLink.error = new Error();
            this.clientSystemLinks.push(clientSystemLink);
            this.reorderLinks();
            this.edit = clientSystemLink;
        },
        undoDelete(clientSystemLink) {
            clientSystemLink.deleted = false;
        },
        deleteLink(clientSystemLink) {
            if (clientSystemLink.id === null) {
                this.clientSystemLinks = this.clientSystemLinks.filter((c) => c !== clientSystemLink);
            } else {
                clientSystemLink.deleted = true;
            }
            this.validate();
        },
        editLink(clientSystemLink) {
            this.edit = this.edit === clientSystemLink ? null : clientSystemLink;
        },
        updateLinks() {
            this.validate();
            if (!this.validFormValues) {
                return;
            }
            this.clientSystemLinks.forEach((clientSystemLink) => {
                clientSystemLink.departments = clientSystemLink.departmentObjects?.map((department) => department.id) ?? null;
            });
            ClubClientSystemLinkRepository.update(this.clubId, this.clientSystemLinks)
                .then(() => {
                    this.$notify.success('saveChanges.success');
                    this.loadLinks();
                })
                .catch((error) => {
                    this.$notify.error('saveChanges.error');
                    this.clientSystemLinks.forEach((clientSystemLink) => {
                        clientSystemLink.error = error.collectionErrorAtIndex('clientSystemLinks', clientSystemLink.position);
                    });
                })
            ;
        },
        isUrlValid(url) {
            try {
                new URL(url);
            } catch (e) {
                return false;
            }
            return true;
        },
    },
};
</script>

<style scoped>
.icon-image-file {
    width: 24px;
    height: 24px;
    vertical-align: text-bottom;
    margin-right: 1rem;
}
table.drag-wrapper tr td {
    padding: 1rem;
}
table.drag-wrapper tr td.handle-column {
    padding: 0;
}

.table-actions {
    vertical-align: top;
    width: 5%;
}

.cls-icon {
    vertical-align: text-bottom;
    margin-right: 1rem;
}

tr.deleted {
    background-color: #fdd !important;
    text-decoration: line-through;
}

table tbody tr .handle {
    cursor: grab;
}

.extra-padding {
    padding: 1rem;
}

tr.dragging {
    background: var(--kgr-color-highlight) !important;
}
</style>
